import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Key, Warning, CheckCircle } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Panel, PanelHeading } from '../components/ui/panel';
import Gate from '../components/access/Gate';
import { accessErrorMessage, DEAD_RECOVERY_LINK } from '../lib/accessCopy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FieldLabel = (props) => (
  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground" {...props} />
);

const ErrorLine = ({ testid, children }) => (
  <div role="alert" className="flex items-start gap-2 text-sm text-destructive">
    <Warning className="w-4 h-4 mt-0.5 shrink-0" weight="fill" aria-hidden="true" />
    <span data-testid={testid}>{children}</span>
  </div>
);

/**
 * Setting a new password from a link the Founder issued.
 *
 * No session is granted here. Holding the link proves you hold the link, not
 * that you are the member, so the last step is still the ordinary sign-in —
 * and two-factor still applies if the account has it.
 *
 * The password rules live on the server and nowhere else; its explanation is
 * shown as it comes. The client checks only that the two fields match.
 */
const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [stage, setStage] = useState('checking'); // checking | ready | done | dead | offline
  const [who, setWho] = useState(null);
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const verify = useCallback(async () => {
    setStage('checking');
    try {
      const { data } = await axios.post(`${API_URL}/api/password-reset/verify`, { token });
      setWho(data);
      setStage('ready');
    } catch (e) {
      // A link that fails for any reason reads the same, so the page cannot
      // be used to find out which links exist. Only a lost connection is
      // told apart, because trying again then actually helps.
      setStage(e.response ? 'dead' : 'offline');
    }
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pass !== pass2) { setError('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/password-reset/complete`, { token, password: pass });
      setPass(''); setPass2('');
      setStage('done');
    } catch (err) {
      setError(accessErrorMessage(
        { status: err.response?.status || 0, error: err.response?.data?.detail },
        'Could not set that password.',
      ));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Gate
      data-testid="reset-screen"
      // The full form: name, two fields, advice, button and the Vault notice.
      reserve={520}
      footer={
        stage === 'ready' && (
          <Link to="/login" className="inline-flex min-h-[44px] items-center hover:text-foreground">
            Single-use link &middot; Return to the gate
          </Link>
        )
      }
    >
      <Panel className="p-5">
        <div className="text-center pb-3 mb-4 border-b border-border">
          <Key className="w-6 h-6 mx-auto mb-2" style={{ color: 'hsl(var(--gold))' }} aria-hidden="true" />
          <PanelHeading>A New Password</PanelHeading>
          {stage === 'ready' && who && (
            <p className="text-sm text-muted-foreground mt-1" data-testid="reset-whose">
              For {who.display_name}
              {who.member_no ? ` — Member No. ${String(who.member_no).padStart(3, '0')}` : ''}
            </p>
          )}
        </div>

        {stage === 'checking' && (
          <p className="text-sm text-muted-foreground text-center" role="status">Checking the link…</p>
        )}

        {stage === 'dead' && (
          <div className="space-y-4">
            <ErrorLine testid="reset-dead">{DEAD_RECOVERY_LINK}</ErrorLine>
            <p className="text-xs text-muted-foreground">
              Links can only be used once and do not last long. Ask the Founder
              for another.
            </p>
            <Button variant="ghost" className="w-full h-11" onClick={() => navigate('/login')}>
              Return to the gate
            </Button>
          </div>
        )}

        {stage === 'offline' && (
          <div className="space-y-4">
            <ErrorLine testid="reset-offline">
              {accessErrorMessage({ status: 0 })}
            </ErrorLine>
            <Button className="w-full h-11 bg-primary hover:bg-primary/90" onClick={verify}>
              Try again
            </Button>
          </div>
        )}

        {stage === 'ready' && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div className="space-y-1.5">
              <FieldLabel htmlFor="np">New password</FieldLabel>
              <Input
                id="np" type="password" value={pass} autoFocus required
                autoComplete="new-password"
                onChange={(e) => setPass(e.target.value)}
                placeholder="At least 12 characters"
                className="h-11"
                data-testid="reset-password"
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="np2">Confirm password</FieldLabel>
              <Input
                id="np2" type="password" value={pass2} required
                autoComplete="new-password"
                onChange={(e) => setPass2(e.target.value)}
                className="h-11"
                data-testid="reset-password-repeat"
              />
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Three or four ordinary words beat a short tangle of symbols &mdash;
              easier to keep in your head, and harder to guess.
            </p>

            {error && <ErrorLine testid="reset-error">{error}</ErrorLine>}

            <Button
              type="submit"
              disabled={busy || !pass || !pass2}
              className="w-full h-11 bg-primary hover:bg-primary/90 font-semibold tracking-wide"
              data-testid="reset-submit"
            >
              {busy ? 'Setting…' : 'Set New Password'}
            </Button>

            {/* The distinction people get wrong, said where it matters. */}
            <p className="text-xs leading-relaxed text-center" style={{ color: 'hsl(var(--gold))' }} data-testid="reset-vault-notice">
              Your Arcanum account password is separate from your Private Vault
              passphrase. This will not unlock or change your Vault.
            </p>
          </form>
        )}

        {stage === 'done' && (
          <div className="space-y-4 text-center" data-testid="reset-done">
            <CheckCircle
              className="w-9 h-9 mx-auto"
              style={{ color: 'hsl(var(--gold))' }}
              weight="fill"
              aria-hidden="true"
            />
            <p className="text-sm text-foreground">Your password is set.</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Every device that was signed in has been signed out, including any
              you did not recognise. The link you used is now spent.
            </p>
            <Button
              className="w-full h-11 bg-primary hover:bg-primary/90 font-semibold tracking-wide"
              onClick={() => navigate('/login')}
            >
              Sign in
            </Button>
          </div>
        )}
      </Panel>
    </Gate>
  );
};

export default ResetPassword;
